<script setup lang="ts">
import {
    ref,
    type Ref,
} from 'vue';

import type {
    GetTMDListResponse,
    TMDItem,
} from './TMD.types';
import { TMDApi } from './TMDApi';

const tmdApi = new TMDApi();
const tmds: Ref<TMDItem[]> = ref([]);
const loading = ref(false);
const totalItems = ref(0);
const search = ref('');

const excludedClientIds = [
    'ffe4c910-3cc6-499e-8929-e7acae9ffdd6', // ABC Motors
    '14bdedf6-7724-4845-808e-d04d53a773d2', // Connected Vehicles
    '85cc9fb1-b945-4afb-8a61-291427639082', // Demo Client
    'f85b0622-914a-4335-a811-73c3eb72a113'  // Presidian
]

/**
 * Asynchronously loads the Target Market Determinations (TMDs) from the server.
 * This function fetches a paginated list of TMDs, optionally filtered by a search term.
 * This will be run on every keypress in the search field.
 */
const loadTmdsAsync = async ( options: { page: number; itemsPerPage: number; search?: string } ) =>
{
    loading.value = true;

    try
    {
        const getTmdResult: GetTMDListResponse = await tmdApi.getTmdListAsync(
            options.page,
            options.itemsPerPage,
            options.search
        );

        // map the items to the TMDItem type and apply formatting to the date
        tmds.value = getTmdResult.items
            .filter( ( item: any ) => !excludedClientIds.includes( item.clientId ) )
            .map( ( item: any ) =>
            ({
                displayName: item.displayName,
                businessName: item.businessName,
                utcDateUploaded: new Date( item.utcDateUploaded ).toLocaleString( "en-AU",
                {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit"
                }),
                documentUrl: item.documentUrl,
                clientId: item.clientId
            }));

        totalItems.value = getTmdResult.totalCount;
    }
    catch( error )
    {
        console.error( 'Error loading TMDs from server:', error );
    }
    finally
    {
        loading.value = false;
    }
};

// navigate to the document URL in a new tab when a row is clicked
const onRowClick = ( item: TMDItem ) =>
{
    if( item.documentUrl )
    {
        window.open( item.documentUrl, '_blank' );
    }
};

</script>

<template>
    <div class="py-12 max-w-5xl mx-auto pl-4 lg:pl-[38px] flex flex-col gap-6">

        <h1 class="font-bold text-2xl -mb-4">
            Target Market Determination
        </h1>

        <p>
            A Target Market Determination (TMD) describes the type of customer a product is
            designed for and how it should be distributed. It forms part of our commitment
            to transparency and responsible product governance.
        </p>

        <h1 class="font-semibold text-xl -mb-4">
            Dealer-issued PROTECT Product, Administered by Connected Vehicles PTY LTD
        </h1>

        <p>
            PROTECT is a pre-paid 3 year (36 months) or 5 year (60 months) plan which covers the cost of refreshing,
            repairing or replacing certain consumable items used in and on a customer’s motor
            vehicle that become degraded due to normal use and wear and tear.
        </p>

        <div>
            <TextField v-model="search"
                variant="outlined"
                density="compact"
                color="#00B8DC"
                placeholder="Find Your TMD" />

            <div class="mb-4 relative">
                <v-progress-circular
                    v-if="loading"
                    indeterminate
                    class="absolute top-1/2 left-1/2 text-cyan-500 transform -translate-x-1/2 -translate-y-1/2 z-10"
                />
                <v-data-table-server
                    class="bg-gray-50 text-cyan-500 font-semibold text-m cursor-pointer tmd-table"
                    :search="search"
                    hide-default-header
                    :headers="[
                        { title: 'Business', key: 'businessName'},
                        { title: 'Display Name', key: 'displayName' },
                        { title: 'Date Uploaded', key: 'utcDateUploaded' }
                    ]"
                    :items="tmds"
                    :loading="loading"
                    :items-length="totalItems"
                    @update:options="( options ) => loadTmdsAsync({ page: options.page, itemsPerPage: options.itemsPerPage, search: options.search })"
                    @click:row="( _, { item } ) => onRowClick( item )"
                />
        </div>

        </div>

        <h1 class="font-semibold text-xl -mb-4">
            Need Help?
        </h1>

        <p>
            If you need assistance locating the correct TMD or have questions about your coverage,
            please call us on:
            <br/>
            <strong>1800 13 13 18</strong>
        </p>

        <p>
            Or email us at:
            <a href="mailto:concierge@carbucks.com.au" target="_blank" class="text-cyan-500 font-semibold">concierge@carbucks.com.au</a>
        </p>
    </div>
</template>

<style scoped>
.tmd-table :deep(.v-data-table__tr) {
    @apply transition-colors duration-200 ease-in-out;
}

.tmd-table :deep(.v-data-table__tr:hover) {
    @apply bg-gray-100;
}

</style>
